/**
 * Wire protocol of a realtime room (one room per canvas). JSON text frames.
 *
 * The database is the source of truth: an op batch is applied in a transaction, gets the next
 * `rev`, and only then is broadcast. Presence (cursors, selection, live drags) is ephemeral and
 * never stored. Clients apply `ops` messages in rev order; the room fills any gap itself.
 */

export interface PeerInfo {
  /** Connection id: one user with two tabs is two peers. */
  id: string;
  userId: string;
  name: string;
  color: string;
  canWrite: boolean;
}

/** Small free-form object: cursor {x,y}, selection [ids], dragging [{id, position}], viewport. */
export type Presence = Record<string, unknown>;

export interface OpRecord {
  rev: number;
  actor: string | null;
  clientId: string | null;
  clientOpId: string | null;
  /** Ops as applied (the server may drop ops that targeted something archived meanwhile). */
  ops: unknown[];
  at: string;
}

export type ClientMsg =
  | { t: "hello"; clientId: string; lastRev?: number }
  | { t: "ops"; clientOpId: string; ops: unknown[] }
  | { t: "presence"; presence: Presence }
  | { t: "sync"; fromRev: number }
  | { t: "ping" };

export type ServerMsg =
  | {
      t: "welcome";
      self: PeerInfo;
      peers: (PeerInfo & { presence: Presence | null })[];
      rev: number;
      /** Full state when the client had none or fell too far behind. */
      snapshot?: unknown;
      /** Missed op records (rev > the client's lastRev), when the gap was small. */
      ops?: OpRecord[];
    }
  | ({ t: "ops" } & OpRecord)
  | { t: "ack"; clientOpId: string; rev: number; duplicate: boolean }
  | { t: "reject"; clientOpId: string; code: string; message: string }
  | { t: "join"; peer: PeerInfo }
  | { t: "leave"; peer: string }
  | { t: "presence"; peer: string; presence: Presence }
  | { t: "resync"; rev: number; snapshot: unknown }
  | { t: "error"; code: string; message: string }
  /** Something changed elsewhere that the client should read again (an inbox room's `inbox`). No payload. */
  | { t: "signal"; kind: string }
  | { t: "pong" };

export const LIMITS = {
  /** Bytes per incoming frame. */
  frame: 256 * 1024,
  opsPerBatch: 200,
  presenceBytes: 4096,
  /** Presence messages per second per peer; extra ones are dropped. */
  presencePerSecond: 30,
  /** Catch up with op records up to this many revs; beyond that the client gets a snapshot. */
  catchUp: 500,
  idLength: 100,
  /** Bytes of op records in one catch-up; beyond that the client gets a snapshot. */
  catchUpBytes: 4 * 1024 * 1024,
  /** Peers per user per room, and per room (one ticket can open several sockets within its minute). */
  peersPerUser: 10,
  peersPerRoom: 200,
  /** A socket that has not said hello by then is closed. */
  helloTimeoutMs: 10_000,
  /** Sockets are re-authorised at least this often: the client reconnects with a fresh ticket. */
  maxLifetimeMs: 30 * 60_000,
} as const;

/** Close codes (4000–4999 are application-defined). */
export const CLOSE = {
  unauthorized: 4401,
  forbidden: 4403,
  notFound: 4404,
  protocol: 4400,
  tooLarge: 4413,
  gone: 4410,
  /** Session lifetime reached: reconnect with a fresh ticket. */
  expired: 4408,
  tooMany: 4429,
} as const;

export class RoomError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "RoomError";
    this.code = code;
  }
}

const isId = (v: unknown): v is string =>
  typeof v === "string" && v.length > 0 && v.length <= LIMITS.idLength;
const isRev = (v: unknown): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0;

/** Parses and validates a client frame; throws RoomError("protocol") on anything malformed. */
export function parseClientMsg(raw: string): ClientMsg {
  if (raw.length > LIMITS.frame) throw new RoomError("too_large", "frame too large");
  let m: unknown;
  try {
    m = JSON.parse(raw);
  } catch {
    throw new RoomError("protocol", "frame is not JSON");
  }
  if (!m || typeof m !== "object" || Array.isArray(m))
    throw new RoomError("protocol", "frame is not an object");
  const o = m as Record<string, unknown>;
  switch (o.t) {
    case "hello":
      if (!isId(o.clientId)) throw new RoomError("protocol", "hello needs clientId");
      if (o.lastRev !== undefined && !isRev(o.lastRev)) throw new RoomError("protocol", "bad lastRev");
      return o.lastRev === undefined
        ? { t: "hello", clientId: o.clientId }
        : { t: "hello", clientId: o.clientId, lastRev: o.lastRev };
    case "ops":
      if (!isId(o.clientOpId)) throw new RoomError("protocol", "ops needs clientOpId");
      if (!Array.isArray(o.ops) || o.ops.length === 0)
        throw new RoomError("protocol", "ops must be a non-empty array");
      if (o.ops.length > LIMITS.opsPerBatch) throw new RoomError("too_large", "too many ops in one batch");
      return { t: "ops", clientOpId: o.clientOpId, ops: o.ops };
    case "presence": {
      const p = o.presence;
      if (!p || typeof p !== "object" || Array.isArray(p))
        throw new RoomError("protocol", "presence must be an object");
      if (JSON.stringify(p).length > LIMITS.presenceBytes)
        throw new RoomError("too_large", "presence too large");
      return { t: "presence", presence: p as Presence };
    }
    case "sync":
      if (!isRev(o.fromRev)) throw new RoomError("protocol", "sync needs fromRev");
      return { t: "sync", fromRev: o.fromRev };
    case "ping":
      return { t: "ping" };
    default:
      throw new RoomError("protocol", `unknown message type ${JSON.stringify(o.t)}`);
  }
}

const PALETTE = ["#e4572e", "#2e86ab", "#a23b72", "#3bb273", "#f18f01", "#7768ae", "#c73e1d", "#1b998b"];

/** Stable color per user, so a person keeps their color across tabs and sessions. */
export function colorFor(userId: string): string {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}
